
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "@/lib/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import AnimatedPage from "@/components/AnimatedPage";

const SubscriptionSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkSubscription, userPlan } = useSubscription();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processSubscription = async () => {
      try {
        // Get session_id from URL query param
        const searchParams = new URLSearchParams(location.search);
        const sessionId = searchParams.get("session_id");

        if (!sessionId) {
          setError("No session ID found. Please contact support.");
          setIsLoading(false);
          return;
        }

        // Update subscription status
        await checkSubscription();
        setIsLoading(false);
      } catch (err) {
        console.error("Error processing subscription:", err);
        setError("Failed to process subscription. Please contact support.");
        setIsLoading(false);
      }
    };

    processSubscription();
  }, [location, checkSubscription]);

  const handleContinue = () => {
    navigate("/home");
  };

  return (
    <AnimatedPage>
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg border bg-background p-8 shadow-sm">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
              <h1 className="text-2xl font-bold">Processing your subscription...</h1>
              <p className="text-center text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <div className="rounded-full bg-destructive p-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
              <h1 className="text-2xl font-bold">Subscription Error</h1>
              <p className="text-center text-muted-foreground">{error}</p>
              <Button onClick={handleContinue} className="mt-4">
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="rounded-full bg-green-100 p-3">
                <div className="rounded-full bg-green-500 p-2">
                  <Check className="h-6 w-6 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold">Subscription Successful!</h1>
              <p className="text-center text-muted-foreground">
                Thank you for subscribing to the {userPlan?.name} plan. Your account has been upgraded.
              </p>
              <div className="mt-2 rounded-lg bg-muted p-4">
                <h3 className="font-medium">Plan Details:</h3>
                <ul className="mt-2 space-y-1">
                  <li>• {userPlan?.max_chit_funds} chit funds</li>
                  <li>• Up to {userPlan?.max_members} members</li>
                  <li>• Enhanced features and support</li>
                </ul>
              </div>
              <Button onClick={handleContinue} className="mt-4">
                Continue to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </AnimatedPage>
  );
};

export default SubscriptionSuccess;
